// FIXME: Update this file to be type safe and remove this and next line
// @ts-strict-ignore
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { BehaviorSubject, debounceTime, firstValueFrom, Subject, takeUntil } from "rxjs";

import { AvatarService } from "@bitwarden/common/auth/abstractions/avatar.service";
import { ProfileResponse } from "@bitwarden/common/models/response/profile.response";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { PlatformUtilsService } from "@bitwarden/common/platform/abstractions/platform-utils.service";
import { Utils } from "@bitwarden/common/platform/misc/utils";
import {
  AvatarDefaultColors,
  defaultAvatarColors,
  DIALOG_DATA,
  DialogConfig,
  DialogRef,
  DialogService,
  ToastService,
} from "@bitwarden/components";

import { DynamicAvatarComponent } from "../../../components/dynamic-avatar.component";
import { SharedModule } from "../../../shared";

import {
  PROFILE_AVATAR_ACCEPT,
  PROFILE_AVATAR_ALLOWED_TYPES,
  PROFILE_AVATAR_MAX_BYTES,
} from "./profile-avatar.constants";
import { SelectableAvatarComponent } from "./selectable-avatar.component";

type ChangeAvatarDialogData = {
  profile: ProfileResponse;
};

// FIXME(https://bitwarden.atlassian.net/browse/CL-764): Migrate to OnPush
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  templateUrl: "change-avatar-dialog.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [SharedModule, SelectableAvatarComponent, DynamicAvatarComponent],
})
export class ChangeAvatarDialogComponent implements OnInit, OnDestroy {
  profile: ProfileResponse;

  // FIXME(https://bitwarden.atlassian.net/browse/CL-903): Migrate to Signals
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @ViewChild("colorPicker") colorPickerElement: ElementRef<HTMLElement>;
  // FIXME(https://bitwarden.atlassian.net/browse/CL-903): Migrate to Signals
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @ViewChild("photoInput") photoInputElement: ElementRef<HTMLInputElement>;

  loading = false;
  pendingPhotoFile: File | null = null;
  pendingPhotoPreviewUrl: string | null = null;
  removeExistingPhoto = false;
  currentPhotoUrl: string | null = null;

  readonly profileAvatarAccept = PROFILE_AVATAR_ACCEPT;

  defaultColorPalette: NamedAvatarColor[] = AvatarDefaultColors.map((color) => {
    return {
      color: defaultAvatarColors[color],
      name: this.i18nService.t(color === "brand" ? "blue" : color),
    };
  });

  customColorSelected = false;
  currentSelection: string;

  protected customColor$ = new BehaviorSubject<string | null>(null);
  protected customTextColor$ = new BehaviorSubject<string>("#000000");
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(DIALOG_DATA) protected data: ChangeAvatarDialogData,
    private i18nService: I18nService,
    private platformUtilsService: PlatformUtilsService,
    private avatarService: AvatarService,
    private dialogRef: DialogRef,
    private toastService: ToastService,
  ) {
    this.profile = data.profile;
  }

  async ngOnInit() {
    this.customColor$
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((color: string | null) => {
        if (color == null) {
          return;
        }
        this.customTextColor$.next(Utils.pickTextColorBasedOnBgColor(color));
        this.customColorSelected = true;
        this.currentSelection = color;
      });

    await this.setSelection(await firstValueFrom(this.avatarService.avatarColor$));
    this.currentPhotoUrl = await firstValueFrom(this.avatarService.avatarImageUrl$);
  }

  get previewPhotoUrl(): string | null {
    if (this.removeExistingPhoto) {
      return null;
    }

    return this.pendingPhotoPreviewUrl ?? this.currentPhotoUrl;
  }

  get hasExistingPhoto(): boolean {
    return this.currentPhotoUrl != null && this.currentPhotoUrl !== "";
  }

  async showCustomPicker() {
    this.customColorSelected = true;
    this.colorPickerElement.nativeElement.click();
    // FIXME: Verify that this floating promise is intentional. If it is, add an explanatory comment and ensure there is proper error handling.
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.setSelection(this.customColor$.value);
  }

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";

    if (file == null) {
      return;
    }

    if (!PROFILE_AVATAR_ALLOWED_TYPES.has(file.type)) {
      this.toastService.showToast({
        variant: "error",
        title: null,
        message: this.i18nService.t("profilePhotoInvalidType"),
      });
      return;
    }

    if (file.size > PROFILE_AVATAR_MAX_BYTES) {
      this.toastService.showToast({
        variant: "error",
        title: null,
        message: this.i18nService.t("profilePhotoTooLarge"),
      });
      return;
    }

    this.clearPendingPhotoPreview();
    this.pendingPhotoFile = file;
    this.pendingPhotoPreviewUrl = URL.createObjectURL(file);
    this.removeExistingPhoto = false;
  }

  markPhotoForRemoval() {
    this.clearPendingPhotoPreview();
    this.removeExistingPhoto = true;
  }

  openPhotoPicker() {
    this.photoInputElement.nativeElement.click();
  }

  submit = async () => {
    const isValidHex = Utils.validateHexColor(this.currentSelection);
    const isValidSelection = this.currentSelection == null || isValidHex;

    if (!isValidSelection) {
      this.toastService.showToast({
        variant: "error",
        title: null,
        message: this.i18nService.t("errorOccurred"),
      });
      return;
    }

    this.loading = true;
    try {
      if (this.removeExistingPhoto) {
        await this.avatarService.removeAvatarImage();
        this.currentPhotoUrl = null;
      } else if (this.pendingPhotoFile != null) {
        await this.avatarService.setAvatarImage(this.pendingPhotoFile);
        this.currentPhotoUrl = await firstValueFrom(this.avatarService.avatarImageUrl$);
        this.clearPendingPhotoPreview();
      }

      await this.avatarService.setAvatarColor(this.currentSelection);
      await this.dialogRef.close();
      this.toastService.showToast({
        variant: "success",
        title: null,
        message: this.i18nService.t("avatarUpdated"),
      });
    } catch {
      this.toastService.showToast({
        variant: "error",
        title: null,
        message: this.i18nService.t("errorOccurred"),
      });
    } finally {
      this.loading = false;
    }
  };

  async ngOnDestroy() {
    this.clearPendingPhotoPreview();
    this.destroy$.next();
    this.destroy$.complete();
  }

  async setSelection(color: string | null) {
    this.defaultColorPalette.filter((x) => x.selected).forEach((c) => (c.selected = false));

    if (color == null) {
      return;
    }

    color = color.toLowerCase();

    this.customColorSelected = false;
    //Allow for toggle
    if (this.currentSelection === color) {
      this.currentSelection = null;
    } else {
      const selectedColorIndex = this.defaultColorPalette.findIndex((c) => c.color === color);
      if (selectedColorIndex !== -1) {
        this.defaultColorPalette[selectedColorIndex].selected = true;
        this.currentSelection = color;
      } else {
        this.customColor$.next(color);
      }
    }
  }

  static open(dialogService: DialogService, config: DialogConfig<ChangeAvatarDialogData>) {
    return dialogService.open(ChangeAvatarDialogComponent, config);
  }

  private clearPendingPhotoPreview() {
    if (this.pendingPhotoPreviewUrl != null) {
      URL.revokeObjectURL(this.pendingPhotoPreviewUrl);
    }
    this.pendingPhotoFile = null;
    this.pendingPhotoPreviewUrl = null;
  }
}

export class NamedAvatarColor {
  name: string;
  color: string;
  selected? = false;
}
